import React, { useContext, useEffect } from "react";
import { CreateMixStyled } from "./CreateMixStyled";
import Flex from "../../UI/Flex/Flex";
import { FLEX_ALIGN, JUSTIFY_CONTENT } from "../../entities/Flex";
import MixtureList from "../../components/MixtureList/MixtureList";
import Customize from "../../components/Customize/Customize";
import Checkout from "../../components/Checkout";
import { useAppDispatch } from "../../store/store";
import { TranslationContextInterface } from "../../entities/Language";
import { TranslationContext } from "../../context/TranslationContext";
import { setBreadcrumbs } from "../../components/Breadcrumbs/slice/BreadcrumbsSlice";
import { getValueForKey } from "../../context/translationsUtils";
import { useSelector } from "react-redux";
import { getDataFromMixture } from "../../components/Mixture/selectors";
import { fetchCalculatedPricesAndVolume } from "../../components/Calculation/slice/action";
import { getTenantCredentials } from "../../components/Tenant/selectors";
import { useNavigate } from "react-router-dom";
import { WebConfiguratorPath } from "../../routes/routes";
import { getCondiments } from "../../components/Condiment/selectors";
import { getCurrentLanguageId } from "../../lang/utils";
import {
  setNameHasError,
  updateSelectedCondiments,
} from "../../components/Mixture/MixtureSlice";
import { fetchCondiments } from "../../components/Condiment/actions";
import { MixtureChooseEnum } from "../../entities/Mixture";
import { TenantCredentialsInterface } from "../../entities/Tenant";

const CreateMix: React.FC = () => {
  const { id: webshopId } = useSelector(
    getTenantCredentials,
  ) as TenantCredentialsInterface;
  const {
    condiments,
    selectedWeight,
    jars,
    mixtureOption,
    totalGrams,
    totalPercentage,
  } = useSelector(getDataFromMixture);
  const dispatch = useAppDispatch();
  const { translations }: TranslationContextInterface =
    useContext(TranslationContext);
  const languageId = getCurrentLanguageId();
  const navigate = useNavigate();
  const { spices = [], herbs = [] } = useSelector(getCondiments);

  useEffect(() => {
    dispatch(
      setBreadcrumbs({
        name: getValueForKey(translations, "nav_bar.nav_card_create_button"),
        level: 1,
        link: null,
      }),
    );
  }, [dispatch, translations]);

  useEffect(() => {
    if (!condiments.length) {
      navigate(WebConfiguratorPath.CUSTOM);
    }
  }, [condiments, navigate]);

  useEffect(() => {
    const isGramsValid = mixtureOption === MixtureChooseEnum.GRAMS;
    const isPercentageValid =
      mixtureOption === MixtureChooseEnum.PERCENTAGE && totalPercentage === 100;
    const isPartsValid = mixtureOption === MixtureChooseEnum.PARTS;

    if (isGramsValid || isPercentageValid || isPartsValid) {
      const debounceDispatch = setTimeout(() => {
        const condimentsForCalculation = condiments.map((item) => {
          const percentage = item.meta.percentage;
          return {
            id: item.id,
            price: item.price,
            percentage,
            grams: item.meta.grams,
            medDensity: item.medDensity,
            parts: item.meta.parts,
            minWeight: item.minWeight,
          };
        });

        dispatch(
          fetchCalculatedPricesAndVolume({
            condiments: condimentsForCalculation,
            selectedWeight,
            numberOfJars: jars,
            webshopId,
            mixtureOption,
          }),
        );
      }, 500);

      return () => {
        clearTimeout(debounceDispatch);
      };
    }
  }, [
    dispatch,
    condiments,
    selectedWeight,
    jars,
    webshopId,
    totalGrams,
    totalPercentage,
    mixtureOption,
  ]);

  useEffect(() => {
    dispatch(fetchCondiments({}));
    dispatch(setNameHasError(false));
  }, [languageId, dispatch]);

  useEffect(() => {
    dispatch(updateSelectedCondiments([...spices, ...herbs]));
  }, [herbs, spices, dispatch]);

  return (
    <CreateMixStyled>
      <Flex
        styles={{
          justify: JUSTIFY_CONTENT.SPACE_BETWEEN,
          align: FLEX_ALIGN.FLEX_START,
        }}
      >
        <div className="create-mix__left">
          <MixtureList />
        </div>
        <div className="create-mix__right">
          <Customize />
          <Checkout />
        </div>
      </Flex>
    </CreateMixStyled>
  );
};

export default CreateMix;
